import { useState } from 'react';

interface SocialShareProps {
  url: string;
  title: string;
}

const SocialShare = ({ url, title }: SocialShareProps) => {
  const [copied, setCopied] = useState(false);

  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const shareLinks = [
    {
      name: 'Facebook',
      icon: 'ri-facebook-fill',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      color: 'bg-[#1877F2] hover:bg-[#166fe5]',
    },
    {
      name: 'X',
      icon: 'ri-twitter-x-fill',
      href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      color: 'bg-gray-900 hover:bg-gray-800',
    },
    {
      name: 'LinkedIn',
      icon: 'ri-linkedin-fill',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      color: 'bg-[#0A66C2] hover:bg-[#0958a8]',
    },
    {
      name: 'Nextdoor',
      icon: 'ri-home-4-line',
      href: `https://nextdoor.com/share/?url=${encodedUrl}`,
      color: 'bg-[#00B14F] hover:bg-[#009a44]',
    },
    {
      name: 'Email',
      icon: 'ri-mail-line',
      href: `mailto:?subject=${encodedTitle}&body=${encodedUrl}`,
      color: 'bg-gray-500 hover:bg-gray-600',
    },
  ];

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = url;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs font-semibold text-gray-500 mr-1">Share:</span>
      {shareLinks.map((link) => (
        <a
          key={link.name}
          href={link.href}
          target="_blank"
          rel="noopener noreferrer nofollow"
          aria-label={`Share on ${link.name}`}
          className={`w-8 h-8 flex items-center justify-center rounded-full text-white transition-colors cursor-pointer ${link.color}`}
          title={`Share on ${link.name}`}
        >
          <i className={`${link.icon} text-sm`}></i>
        </a>
      ))}
      <button
        onClick={handleCopy}
        className={`w-8 h-8 flex items-center justify-center rounded-full text-white transition-colors cursor-pointer ${
          copied ? 'bg-[#1B6B3A]' : 'bg-gray-400 hover:bg-gray-500'
        }`}
        title={copied ? 'Copied!' : 'Copy link'}
        aria-label="Copy link to clipboard"
      >
        <i className={`${copied ? 'ri-check-line' : 'ri-link'} text-sm`}></i>
      </button>
      {copied && (
        <span className="text-xs text-[#1B6B3A] font-semibold animate-pulse">Copied!</span>
      )}
    </div>
  );
};

export default SocialShare;